import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { LogOut } from 'lucide-react';
import { NAV } from '@/config/nav';
import { useAuth } from '@/hooks/useAuth';
import { BrandTile } from './BrandMark';

// Styled after slightercrm/src/components/Sidebar.tsx so the two consoles share
// one navigation language; the admin nav is short enough to stay uncollapsed.
export const Sidebar: React.FC = () => {
  const { logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (
    <aside aria-label="Platform navigation" className="flex h-screen w-16 shrink-0 flex-col border-r border-border bg-surface md:w-56 xl:w-60">
      <div className="px-3 pb-3 pt-5 md:px-4">
        <div className="flex items-center gap-2.5">
          <BrandTile size={36} />
          <div className="hidden flex-col leading-tight md:flex">
            <span className="font-display text-[15px] font-bold tracking-tight text-text-primary">Slightor</span>
            <span className="text-2xs tracking-wide text-text-muted">Platform Console</span>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3 pt-1 md:px-3">
        {NAV.map((g) => (
          <div key={g.group} className="mb-1">
            <div className="hidden px-3 pb-1 pt-3 text-[10px] font-medium uppercase tracking-[0.14em] text-text-muted md:block">{g.group}</div>
            <div className="flex flex-col gap-0.5">
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={item.label}
                  title={item.label}
                  className={({ isActive }) => clsx(
                    'group flex items-center justify-center gap-2.5 rounded-lg px-2 py-2 text-[13px] outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 md:justify-start md:px-3 md:py-1.5',
                    (isActive || (!!item.activePrefix && pathname.startsWith(item.activePrefix)))
                      ? 'bg-primary-soft font-semibold text-primary'
                      : 'font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary',
                  )}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        size={16}
                        className={clsx('shrink-0 transition-colors', (isActive || (!!item.activePrefix && pathname.startsWith(item.activePrefix))) ? 'text-primary' : 'text-text-muted group-hover:text-text-primary')}
                      />
                      <span className="hidden flex-1 truncate md:block">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-2 md:p-3">
        <div className="flex items-center justify-center gap-2 rounded-xl border border-border bg-surface-alt p-2">
          <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white md:flex">SA</div>
          <div className="hidden min-w-0 flex-1 md:block">
            <div className="truncate text-[13px] font-semibold text-text-primary">Super Admin</div>
            <div className="truncate text-2xs text-text-muted">Platform administrator</div>
          </div>
          <button
            type="button"
            onClick={() => { logout(); nav('/login', { replace: true }); }}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
};
